import { ErrorAlert } from '@/components/_ui/ErrorAlert'
import { Separator } from '@/components/_ui/Separator'
import { Skeleton } from '@/components/_ui/Skeleton'
import { DistributorOnly } from '@/components/Helpers/DistributorOnly'
import T from '@/components/Helpers/Translate'
import { usePriceHandler } from '@/lib/hooks/helpers/usePriceHandler'
import type { HydraErrorType } from '@/lib/services/Hydra/_hydraError'
import type { HydraGetSubscriptionItem } from '@/lib/services/Hydra/account/GET/getSubscriptions'
import type { QuotesCallType } from '@/lib/services/Hydra/quotesCallType'
import { cn } from '@/lib/utils'
import type { CartItemType } from '@/store/types/cart'
import useGlobalStore from '@/store/useGlobalStore'
import Image from 'next/image'
import { ConfirmAddToActiveSubscriptionSavings } from '../ConfirmAddToActiveSubscriptionSavings'

const Row = ({
  children,
  className,
}: {
  children: JSX.Element | JSX.Element[]
  className?: string
}) => (
  <div className={cn('flex items-center justify-between', className)}>
    {children}
  </div>
)

type Props = {
  orderCalculation: (QuotesCallType & HydraErrorType) | null
  isLoadingOrderCalculation: boolean
  subscription: HydraGetSubscriptionItem
  itemToAddToSubscription: CartItemType
}

export const ConfirmAddToActiveSubscriptionTotals = ({
  orderCalculation,
  isLoadingOrderCalculation,
  subscription,
  itemToAddToSubscription,
}: Props) => {
  const {
    app: { marketExceptions },
  } = useGlobalStore()
  const { priceHandler } = usePriceHandler()
  if (isLoadingOrderCalculation) {
    return (
      <div className="space-y-2 px-3 sm:px-12">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Separator className="my-3" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  if (!orderCalculation || !subscription || !subscription.transactions.items) {
    return <ErrorAlert error={{ error: { message: 'Unexpected Error' } }} />
  }

  if (orderCalculation.error) {
    return <ErrorAlert error={orderCalculation} />
  }

  if (!orderCalculation.items || !orderCalculation.items[0].terms) {
    return <ErrorAlert error={{ error: { message: 'Unexpected Error' } }} />
  }

  const displayShippingPriceWithErrorHandling = () => {
    if (!orderCalculation.items || !orderCalculation.items[0].terms.freight) {
      return <T>error</T>
    }

    const shipping = orderCalculation.items[0].terms.freight.amount

    if (shipping === 0) return <T>free</T>

    return priceHandler({
      price: orderCalculation.items[0].terms.freight.amount,
    })
  }

  const displayTaxes = () => {
    if (!orderCalculation.items || !orderCalculation.items[0].terms.freight) {
      return <T>error</T>
    }
    const taxes = orderCalculation.items[0].terms.tax.amount

    if (taxes === 0) return <T>free</T>

    return priceHandler({ price: taxes })
  }
  return (
    <div className="px-3 sm:px-12">
      <div className="space-y-2">
        <Row>
          <span>
            <T>payment</T>
          </span>
          <span className="space-x-1">
            <T>card_ending_in</T>
            <span>
              {subscription.transactions.items[0].methodDetails.creditCardNumberMasked?.slice(
                -4,
              )}
            </span>
          </span>
        </Row>
        <Row>
          <span>
            {marketExceptions?.tax?.inclusive ? <T>VAT</T> : <T>taxes</T>}
          </span>
          <span>{displayTaxes()}</span>
        </Row>
        <Row>
          <span>
            <T>shipping</T>
          </span>
          <span>{displayShippingPriceWithErrorHandling()}</span>
        </Row>
        <ConfirmAddToActiveSubscriptionSavings
          itemToAddToSubscription={itemToAddToSubscription}
        />

        <Separator className="my-3" />
        <Row className="text-lg font-bold">
          <span>
            <T>new_total</T>
          </span>
          <span>
            {' '}
            {priceHandler({
              price: orderCalculation.items[0].terms.total,
            })}
          </span>
        </Row>
        <DistributorOnly>
          <Row>
            <span>
              <T>new_total_pv</T>
            </span>
            <span>{orderCalculation.items[0].terms.pv}</span>
          </Row>
        </DistributorOnly>
      </div>

      <div className="mt-2 p-3 text-[#478889]">
        <div className="flex justify-between">
          <span>
            <Image
              className="color-[#478889] inline"
              src="/icons/green-info.svg"
              height={15}
              width={15}
              alt="info"
            />
            <p className="ml-2 inline">
              <T>you_wont_be_charged_right_now</T>
            </p>
          </span>
        </div>
      </div>
    </div>
  )
}
